import { format, startOfWeek, addDays, getDay } from "date-fns";
export type Weekdays = 1 | 2 | 3 | 4 | 5 | 6 | 0;
export type Fields = "from" | "to" | "breakFrom" | "breakUntil" | "total" | "date";

export const defaultWeekDaysLabels: Record<Weekdays, string> = {
  0: "Sunday",
  1: "Monday",
  2: "Tuesday",
  3: "Wednesday",
  4: "Thursday",
  5: "Friday",
  6: "Saturday",
};

export const fieldLabels: Record<Fields, string> = {
  from: "From",
  to: "To",
  breakFrom: "Break From",
  breakUntil: "Break To",
  total: "Total",
  date: "Date"
};

export interface WeeklyPlanningProps {
  weekdays?: Weekdays[];
  fields?: Fields[];
  onChange?: (timeStates: Record<Weekdays, Record<Fields, string>>) => void;
}

export const defaultWeekdays: Weekdays[] = [1, 2, 3, 4, 5, 6, 0];

export const defaultFields: Fields[] = [
  "from",
  "to",
  "total",
  "breakFrom",
  "breakUntil",
  "date"
];

// Format time input
export const formatTime = (value: string) => {
  value = value.replace(/[^0-9]/g, ""); // Remove non-numeric characters
  if (value.length <= 2) return value;
  if (value.length <= 4) return `${value.slice(0, 2)}:${value.slice(2, 4)}`;
  return `${value.slice(0, 2)}:${value.slice(2, 4)}`;
};

// Format total value
export const formatTotalValue = (value: string) => {
  value = value.replace(/[^0-9,]/g, ""); // Remove non-numeric characters and allow commas
  return value;
};

// Convert time to minutes
export const timeToMinutes = (time: string) => {
  const [hours, minutes] = time.split(":").map(Number);
  return !isNaN(hours) && !isNaN(minutes) ? hours * 60 + minutes : 0;
};

// Convert minutes to time format
export const minutesToTime = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
};

// Validate time format
export const isValidTime = (time: string) =>
  /^([01]\d|2[0-3]):([0-5]\d)$/.test(time);

// Convert total minutes to percentage format
export const convertMinutesToPercentage = (totalMinutes: number): string => {
  if (isNaN(totalMinutes) || totalMinutes < 0) return "0,00";
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const minutePercentage = Math.round((minutes / 60) * 100);

  // Format as "hours,minutePercentage"
  return `${hours},${minutePercentage.toString().padStart(2, "0")}`;
};

// Convert hh:mm to percentage format
export const convertTimeToPercentage = (time: string): string => {
  const totalMinutes = timeToMinutes(time);
  return convertMinutesToPercentage(totalMinutes);
};

export const calculateTotalTime = (
  from: string,
  to: string,
  breakFrom: string,
  breakUntil: string
) => {
  let totalMinutes = 0;

  if (isValidTime(from) && from) {
    const fromMinutes = timeToMinutes(from);
    let toMinutes = 24 * 60; // Default to 24:00 if until is not provided

    if (isValidTime(to) && to) {
      toMinutes = timeToMinutes(to);
      totalMinutes = toMinutes - fromMinutes;

      if (totalMinutes < 0) totalMinutes += 24 * 60; // Handle overnight shifts
    } else {
      // If 'until' is not provided, assume end of the day
      totalMinutes = 24 * 60 - fromMinutes;
    }

    if (isValidTime(breakFrom) && breakFrom) {
      if (breakFrom === "00:00") {
        // Special case: breakFrom is "00:00", total = until - by
        totalMinutes = toMinutes - fromMinutes;
        if (totalMinutes < 0) totalMinutes += 24 * 60; // Handle overnight shifts
      } else {
        const breakFromMinutes = timeToMinutes(breakFrom);

        if (isValidTime(breakUntil) && breakUntil) {
          if (breakUntil === "00:00") {
            // Special case: breakUntil is "00:00", calculate total as breakFrom - by
            totalMinutes = breakFromMinutes - fromMinutes;
            if (totalMinutes < 0) totalMinutes += 24 * 60; // Handle overnight shifts
          } else {
            const breakUntilMinutes = timeToMinutes(breakUntil);
            const breakDuration = breakUntilMinutes - breakFromMinutes;

            if (breakDuration > 0) {
              totalMinutes -= breakDuration;
            }
          }
        } else {
          totalMinutes = breakFromMinutes - fromMinutes;
          if (totalMinutes < 0) totalMinutes += 24 * 60; // Handle overnight shifts
        }
      }
    }
  }

  return totalMinutes > 0 ? minutesToTime(totalMinutes) : "00:00";
};

export type TimeStates = Record<Weekdays, Record<Fields, string>>;
// Initial time states with default values

export const initialTimeStates: TimeStates = {
  0: { from: "", to: "", total: "0,00", breakFrom: "", breakUntil: "", date: "" },
  1: { from: "", to: "", total: "0,00", breakFrom: "", breakUntil: "", date: "" },
  2: { from: "", to: "", total: "0,00", breakFrom: "", breakUntil: "", date: "" },
  3: { from: "", to: "", total: "0,00", breakFrom: "", breakUntil: "", date: "" },
  4: { from: "", to: "", total: "0,00", breakFrom: "", breakUntil: "", date: "" },
  5: { from: "", to: "", total: "0,00", breakFrom: "", breakUntil: "", date: "" },
  6: { from: "", to: "", total: "0,00", breakFrom: "", breakUntil: "", date: "" },
};

export type ErrorStates = Record<Weekdays, Record<Fields, string>>;

// Initial error state (empty values)
export const initialErrors: ErrorStates = {
  0: { from: "", to: "", breakFrom: "", breakUntil: "", total: "", date: "" },
  1: { from: "", to: "", breakFrom: "", breakUntil: "", total: "", date: "" },
  2: { from: "", to: "", breakFrom: "", breakUntil: "", total: "", date: "" },
  3: { from: "", to: "", breakFrom: "", breakUntil: "", total: "", date: "" },
  4: { from: "", to: "", breakFrom: "", breakUntil: "", total: "", date: "" },
  5: { from: "", to: "", breakFrom: "", breakUntil: "", total: "", date: "" },
  6: { from: "", to: "", breakFrom: "", breakUntil: "", total: "", date: "" },
};

// Utility function for deep comparison
export const isEqual = (obj1: TimeStates, obj2: TimeStates) => {
  return JSON.stringify(obj1) === JSON.stringify(obj2);
};

export const calculateWeekTotal = (timeStates: TimeStates) => {
  const weekdays: Weekdays[] = [0, 1, 2, 3, 4, 5, 6];

  const totalMinutes = weekdays.reduce((acc: number, day: Weekdays) => {
    const totalTime = timeStates?.[day]?.total || "00,00";

    const [rawHours, rawPercentage] = totalTime.split(",");

    const hours = Number.isFinite(parseInt(rawHours, 10))
      ? parseInt(rawHours, 10)
      : 0;

    const percentage = Number.isFinite(parseInt(rawPercentage, 10))
      ? parseInt(rawPercentage, 10)
      : 0;

    const minutes = Math.round((percentage / 100) * 60);

    return acc + hours * 60 + minutes;
  }, 0);

  const hours = Math.floor(totalMinutes / 60) || 0;
  const minutes = totalMinutes % 60 || 0;

  const minutePercentage = Math.round((minutes / 60) * 100) || 0;

  // Format as "hours,minutePercentage"
  return `${hours},${minutePercentage.toString().padStart(2, "0")}`;
};

// Ensure Weekdays is defined as an enum or a specific type
export const generateWeekLabelsFromMonday = (
  from: Date | null | number,
  to?: Date | null | number,
  disabledDays: Weekdays[] = [] // Optional parameter for disabled days
): {
  labels: Record<Weekdays, string>;
  disabledDays: Weekdays[];
  quickEntryDays: Weekdays[];
  dates: { weekday: Weekdays; date: string }[];
} => {
  // Convert "from" to a Date object if it's a number
  if (typeof from === "number") {
    from = new Date(from);
  }

  // Ensure "from" is a valid Date object
  if (from instanceof Date && !isNaN(from.getTime())) {
    const labels: Record<Weekdays, string> = {} as Record<Weekdays, string>;
    const disabledWeekdays: Weekdays[] = [];
    const quickEntryWeekdays: Weekdays[] = [];
    const dates: { weekday: Weekdays; date: string }[] = [];

    // Start of the week (Monday)
    const startOfTheWeek = startOfWeek(from, { weekStartsOn: 1 });
    let currentDate = startOfTheWeek;

    // Helper to strip time from a date
    const normalizeDate = (date: Date) =>
      new Date(date.getFullYear(), date.getMonth(), date.getDate());

    const normalizedFrom = normalizeDate(from);
    const normalizedTo = to ? normalizeDate(new Date(to)) : null;

    const formatDate = (date: Date) => format(date, "yyyy-MM-dd");
    // Generate labels for each day of the week
    for (let i = 0; i < 7; i++) {
      const formattedDate = format(currentDate, "dd/MM");
      const formattedLabel = `${formattedDate} ${format(currentDate, "EEEE")}`;

      const weekday = getDay(currentDate) as Weekdays;

      // Check if the day should be disabled
      const isDisabled = normalizedTo
        ? normalizeDate(currentDate) < normalizedFrom ||
        normalizeDate(currentDate) > normalizedTo ||
        disabledDays.includes(weekday)
        : disabledDays.includes(weekday);

      labels[weekday] = formattedLabel;

      const formattedEntireDate = formatDate(currentDate);
      dates.push({ weekday, date: formattedEntireDate });

      if (isDisabled) {
        disabledWeekdays.push(weekday);
      } else {
        quickEntryWeekdays.push(weekday);
      }

      // Move to the next day
      currentDate = addDays(currentDate, 1);
    }

    return {
      labels,
      disabledDays: disabledWeekdays,
      quickEntryDays: quickEntryWeekdays,
      dates
    };
  }

  return {
    labels: defaultWeekDaysLabels,
    disabledDays: [],
    quickEntryDays: [1, 2, 3, 4, 5],
    dates: []
  };
};

export const getNextSunday = (fromDate: Date) => {
  const dayOfWeek = fromDate.getDay(); // Get the day of the week (0 for Sunday, 6 for Saturday)

  // If the "From" date is already a Sunday, return that date
  if (dayOfWeek === 0) {
    return fromDate;
  }

  // Otherwise, calculate the next Sunday
  const nextSunday = new Date(fromDate);
  nextSunday.setDate(fromDate.getDate() + (7 - dayOfWeek)); // Calculate the upcoming Sunday
  return nextSunday;
};
